(() => {
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const money = (cents, currency = 'TRY') => new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(cents || 0) / 100);

  const shopRoute = (path) => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.endsWith('/') ? root : `${root}/`}${String(path).replace(/^\//, '')}`;
  };

  const waitForElementDefinition = (name, timeout = 2500) => Promise.race([
    window.customElements?.whenDefined(name) || Promise.resolve(),
    new Promise((resolve) => window.setTimeout(resolve, timeout)),
  ]);

  function syncCartTriggerState(isOpen) {
    document.querySelectorAll('[data-fd-cart-toggle]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(isOpen));
    });
  }

  async function openCartDrawer() {
    await waitForElementDefinition('theme-drawer');
    const drawer = document.querySelector('#cart-drawer');

    if (!drawer) {
      window.location.assign(shopRoute('cart'));
      return;
    }

    if (typeof drawer.open === 'function') {
      drawer.open();
    } else {
      const panel = drawer.querySelector('dialog[ref="panel"], dialog');

      if (panel && !panel.open) {
        try {
          panel.showModal();
        } catch (error) {
          panel.setAttribute('open', '');
        }
      }

      drawer.setAttribute('open', '');
    }

    syncCartTriggerState(true);
  }

  async function closeCartDrawer() {
    await waitForElementDefinition('theme-drawer');

    const drawer = document.querySelector('#cart-drawer');

    if (!drawer) return;

    if (typeof drawer.close === 'function') {
      await drawer.close();
    } else {
      const panel = drawer.querySelector('dialog[ref="panel"], dialog');

      if (panel?.open) panel.close();

      drawer.removeAttribute('open');
    }

    syncCartTriggerState(false);
  }

  async function toggleCartDrawer() {
    const drawer = document.querySelector('#cart-drawer');

    if (drawer?.hasAttribute('open')) {
      await closeCartDrawer();
    } else {
      await openCartDrawer();
    }
  }

  function replaceCartDrawerSection(html) {
    if (!html) return false;

    const template = document.createElement('template');
    template.innerHTML = html.trim();

    const nextSection = template.content.querySelector('#shopify-section-cart-drawer-section');
    const currentSection = document.querySelector('#shopify-section-cart-drawer-section');

    if (nextSection && currentSection) {
      currentSection.replaceWith(nextSection);
      return true;
    }

    if (currentSection) {
      currentSection.replaceChildren(template.content.cloneNode(true));
      return true;
    }

    if (nextSection) {
      document.body.append(nextSection);
      return true;
    }

    return false;
  }

  async function refreshCartDrawer(addResponse) {
    let html = addResponse?.sections?.['cart-drawer-section'];

    if (!html) {
      const response = await fetch(`${shopRoute('cart')}?section_id=cart-drawer-section`, {
        headers: {
          Accept: 'text/html',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (response.ok) {
        html = await response.text();
      }
    }

    return replaceCartDrawerSection(html);
  }

  async function refreshCartBubble() {
    const response = await fetch(shopRoute('cart.js'), {
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    });

    if (!response.ok) return;

    const cart = await response.json();
    const count = Number(cart.item_count || 0);

    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', count === 0);
      bubble.toggleAttribute('data-maintain-ratio', count <= 99);

      const countElement = bubble.querySelector('[data-testid="cart-bubble"]');

      if (countElement) {
        countElement.textContent = count < 100 ? String(count) : '';
        countElement.classList.toggle('hidden', count === 0);
      }
    });
  }

  function showCartFormError(form, message) {
    let status = form.closest('product-form-component')?.querySelector('[ref="liveRegion"]');

    if (!status) {
      status = document.createElement('p');
      status.className = 'fd-cart-error';
      status.setAttribute('role', 'alert');
      form.append(status);
    }

    status.textContent = message || 'Ürün sepete eklenemedi. Lütfen tekrar deneyin.';
  }

  /*
   * Bundle ürünlerinin seçili renklerini hazırlar.
   *
   * Hem normal sepete ekleme hem de kredi kartı checkout
   * aynı ürün/renģ bilgilerini kullanır.
   */
  function buildBundleCartItems(form) {
    const quantityInput = form.querySelector('input[name="quantity"]');
    const expectedQuantity = Math.max(
      1,
      Number(quantityInput?.value || 1)
    );

    const selectedBundleColors = [...form.querySelectorAll('[data-bundle-color]:checked')]
      .filter((input) => !input.disabled);

    const hasBundleColorPicker = Boolean(
      form.querySelector('[data-bundle-colors]')
    );

    if (
      hasBundleColorPicker &&
      selectedBundleColors.length !== expectedQuantity
    ) {
      throw new Error(
        'Lütfen paketteki her çanta için bir renk seçin.'
      );
    }

    const packageName =
      form.querySelector('[data-bundle-property]')?.value ||
      `${expectedQuantity} Adet`;

    const group = `fd-crossbody-${Date.now()}`;

    const items = selectedBundleColors.map((input, index) => ({
      id: Number(input.dataset.variantId || input.value),
      quantity: 1,
      properties: {
        Paket: packageName,
        'Çanta Rengi': input.value,
        'pmpr_cart_item': group,
        [`${index + 1}. Çanta Rengi`]: input.value,
        '_Paket Sırası': `${index + 1}/${expectedQuantity}`,
      },
    }));

    /*
     * Bundle renk seçimi olmayan ürünlerde normal variant
     * + quantity sistemi kullanılır.
     */
    if (!hasBundleColorPicker) {
      const variantInput = form.querySelector('input[name="id"]');

      if (!variantInput?.value) {
        throw new Error('Lütfen bir ürün seçin.');
      }

      return {
        items: [{
          id: Number(variantInput.value),
          quantity: expectedQuantity,
        }],
        expectedQuantity,
      };
    }

    return {
      items,
      expectedQuantity,
    };
  }

  async function addFormToCart(form, submitter) {
    if (form.dataset.fdCartBusy === 'true') return;

    const variantInput = form.querySelector('input[name="id"]');
    const quantityInput = form.querySelector('input[name="quantity"]');

    const expectedQuantity = Math.max(
      1,
      Number(quantityInput?.value || 1)
    );

    const selectedBundleColors = [
      ...form.querySelectorAll('[data-bundle-color]:checked')
    ].filter((input) => !input.disabled);

    const hasBundleColorPicker = Boolean(
      form.querySelector('[data-bundle-colors]')
    );

    const colorError = form.querySelector('[data-bundle-colors-error]');

    if (
      hasBundleColorPicker &&
      selectedBundleColors.length !== expectedQuantity
    ) {
      if (colorError) colorError.hidden = false;

      showCartFormError(
        form,
        'Lütfen paketteki her çanta için bir renk seçin.'
      );

      const emptyRow = [
        ...form.querySelectorAll(
          '[data-bundle-color-row]:not([hidden])'
        )
      ].find(
        (row) => !row.querySelector('[data-bundle-color]:checked')
      );

      (
        emptyRow?.querySelector('[data-color-select-trigger]') ||
        form.querySelector(
          '[data-bundle-color-row]:not([hidden]) input:not(:checked)'
        )
      )?.focus();

      return;
    }

    if (colorError) colorError.hidden = true;

    if (!variantInput?.value) {
      showCartFormError(form, 'Lütfen bir ürün seçin.');
      return;
    }

    form.dataset.fdCartBusy = 'true';

    const button =
      submitter || form.querySelector('[type="submit"]');

    const wasDisabled = Boolean(button?.disabled);

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      let body;

      let headers = {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      };

      if (hasBundleColorPicker) {
        const packageName =
          form.querySelector('[data-bundle-property]')?.value ||
          `${expectedQuantity} Adet`;

        const group = `fd-crossbody-${Date.now()}`;

        const items = selectedBundleColors.map((input, index) => ({
          id: Number(input.dataset.variantId || input.value),
          quantity: 1,
          properties: {
            Paket: packageName,
            'Çanta Rengi': input.value,
            'pmpr_cart_item': group,
            [`${index + 1}. Çanta Rengi`]: input.value,
            '_Paket Sırası': `${index + 1}/${expectedQuantity}`,
          },
        }));

        body = JSON.stringify({
          items,
          sections: ['cart-drawer-section'],
          sections_url:
            `${window.location.pathname}${window.location.search}`,
        });

        headers = {
          ...headers,
          'Content-Type': 'application/json',
        };
      } else {
        body = new FormData(form);

        body.set(
          'sections',
          'cart-drawer-section'
        );

        body.set(
          'sections_url',
          `${window.location.pathname}${window.location.search}`
        );
      }

      const response = await fetch(
        shopRoute('cart/add.js'),
        {
          method: 'POST',
          headers,
          body,
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Ürün sepete eklenemedi.'
        );
      }

      let drawerRefreshed = false;

      try {
        drawerRefreshed = await refreshCartDrawer(result);
      } catch (error) {
        console.warn(
          '[FD cart] Drawer could not be refreshed after a successful add.',
          error
        );
      }

      refreshCartBubble().catch((error) =>
        console.warn(
          '[FD cart] Cart count could not be refreshed.',
          error
        )
      );

      if (button) button.dataset.added = 'true';

      if (drawerRefreshed) {
        await openCartDrawer();
      } else {
        window.location.assign(shopRoute('cart'));
      }

      window.setTimeout(() => {
        if (button) button.dataset.added = 'false';
      }, 1800);

    } catch (error) {
      console.error('[FD cart] Add failed:', error);
      showCartFormError(form, error?.message);

    } finally {
      form.dataset.fdCartBusy = 'false';

      if (button) {
        button.disabled = wasDisabled;
        button.removeAttribute('aria-busy');
      }
    }
  }

  function initCartForm(form) {
    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        addFormToCart(
          form,
          event.submitter
        );
      },
      true
    );
  }

  /*
   * ============================================================
   * KREDİ KARTI CHECKOUT
   * ============================================================
   *
   * Bu akış EasySell'e dokunmaz.
   *
   * 1. Seçili bundle/renģleri belirler.
   * 2. Aynı ürünleri Shopify cart'a ekler.
   * 3. Sepeti drawer'a açmaz.
   * 4. Direkt Shopify checkout'a gider.
   *
   * Shopify checkout içerisinde bağlı iyzico ödeme yöntemi
   * kullanılabilir.
   */
  async function checkoutWithCard(root, form, button) {
    if (form.dataset.fdCardBusy === 'true') return;

    const colorError =
      form.querySelector('[data-bundle-colors-error]');

    try {
      const quantityInput =
        form.querySelector('input[name="quantity"]');

      const expectedQuantity = Math.max(
        1,
        Number(quantityInput?.value || 1)
      );

      const selectedBundleColors = [
        ...form.querySelectorAll('[data-bundle-color]:checked')
      ].filter((input) => !input.disabled);

      const hasBundleColorPicker = Boolean(
        form.querySelector('[data-bundle-colors]')
      );

      /*
       * Renk seçimi zorunluluğu.
       */
      if (
        hasBundleColorPicker &&
        selectedBundleColors.length !== expectedQuantity
      ) {
        if (colorError) colorError.hidden = false;

        showCartFormError(
          form,
          'Lütfen paketteki her çanta için bir renk seçin.'
        );

        const emptyRow = [
          ...form.querySelectorAll(
            '[data-bundle-color-row]:not([hidden])'
          )
        ].find(
          (row) =>
            !row.querySelector('[data-bundle-color]:checked')
        );

        (
          emptyRow?.querySelector(
            '[data-color-select-trigger]'
          ) ||
          form.querySelector(
            '[data-bundle-color-row]:not([hidden]) input:not(:checked)'
          )
        )?.focus();

        return;
      }

      if (colorError) colorError.hidden = true;

      const variantInput =
        form.querySelector('input[name="id"]');

      if (!variantInput?.value) {
        showCartFormError(
          form,
          'Lütfen bir ürün seçin.'
        );
        return;
      }

      form.dataset.fdCardBusy = 'true';

      const wasDisabled = Boolean(button?.disabled);

      if (button) {
        button.disabled = true;
        button.setAttribute(
          'aria-busy',
          'true'
        );
      }

      let items;

      /*
       * Bundle ürünlerinde mevcut Fashion Dorella
       * renk/ürün yapısını birebir kullanıyoruz.
       */
      if (hasBundleColorPicker) {
        const packageName =
          form.querySelector(
            '[data-bundle-property]'
          )?.value ||
          `${expectedQuantity} Adet`;

        const group =
          `fd-crossbody-${Date.now()}`;

        items = selectedBundleColors.map(
          (input, index) => ({
            id: Number(
              input.dataset.variantId ||
              input.value
            ),
            quantity: 1,
            properties: {
              Paket: packageName,
              'Çanta Rengi': input.value,

              /*
               * Teknik grup bilgisi:
               * Shopify checkout'ta müşteriye gösterilmemesi için
               * property adının başında "_" kullanıyoruz.
               */
              '_pmpr_cart_item': group,

              [`${index + 1}. Çanta Rengi`]:
                input.value,

              '_Paket Sırası':
                `${index + 1}/${expectedQuantity}`,
            },
          })
        );

      } else {
        items = [{
          id: Number(variantInput.value),
          quantity: expectedQuantity,
        }];
      }

      /*
       * ========================================================
       * EK CÜZDAN
       * ========================================================
       *
       * Mevcut cüzdan mantığı korunuyor.
       */
      const walletToggle =
        form.querySelector(
          '[data-wallet-addon-toggle]'
        );

      const walletColor =
        form.querySelector(
          '[data-wallet-addon-color]'
        );

      if (walletToggle?.checked) {
        const walletOption =
          walletColor?.selectedOptions?.[0];

        if (
          !walletOption ||
          walletOption.disabled ||
          walletOption.dataset.available === 'false'
        ) {
          throw new Error(
            'Ek cüzdan için stokta bir renk seçin.'
          );
        }

        items.push({
          id: Number(walletOption.value),
          quantity: 1,
          properties: {
            'Ek Ürün':
              'Fashion Viral Cüzdan',

            'Cüzdan Rengi':
              walletOption.dataset.color ||
              walletOption.textContent.trim(),

            'Çanta Eklenti Fiyatı':
              '699 TL',
          },
        });
      }

      /*
       * ========================================================
       * ÖNCE MEVCUT SHOPIFY SEPETİNİ TEMİZLE
       * ========================================================
       *
       * Müşteri daha önce checkout'a girip geri döndüyse,
       * eski ürünlerin yeni kredi kartı siparişine taşınmasını
       * engellemek için sepeti tamamen temizliyoruz.
       */
      const clearResponse = await fetch(
        shopRoute('cart/clear.js'),
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        }
      );

      if (!clearResponse.ok) {
        const clearResult =
          await clearResponse.json().catch(
            () => ({})
          );

        throw new Error(
          clearResult.description ||
          clearResult.message ||
          'Mevcut sepet temizlenemedi.'
        );
      }

      /*
       * ========================================================
       * SADECE ŞU AN SEÇİLEN ÜRÜNLERİ SEPETE EKLE
       * ========================================================
       */
      const response = await fetch(
        shopRoute('cart/add.js'),
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With':
              'XMLHttpRequest',
          },
          body: JSON.stringify({
            items,
          }),
        }
      );

      const result =
        await response.json().catch(
          () => ({})
        );

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Ürün checkout için sepete eklenemedi.'
        );
      }

      /*
       * ========================================================
       * DİREKT SHOPIFY CHECKOUT
       * ========================================================
       *
       * Shopify checkout içerisindeki mevcut ödeme sistemi
       * (iyzico dahil) burada devreye girer.
       */
      window.location.assign(
        shopRoute('checkout')
      );

    } catch (error) {
      console.error(
        '[FD Card Checkout] Checkout failed:',
        error
      );

      showCartFormError(
        form,
        error?.message ||
        'Kredi kartı ödeme sayfası açılamadı. Lütfen tekrar deneyin.'
      );

    } finally {
      form.dataset.fdCardBusy = 'false';

      if (button) {
        button.disabled = false;
        button.removeAttribute(
          'aria-busy'
        );
      }
    }
  }

  async function openEasySellForm(root, form, button) {
    const wasDisabled = Boolean(button.disabled);

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const expectedQuantity = Math.max(
        1,
        Number(
          form.querySelector(
            'input[name="quantity"]'
          )?.value || 1
        )
      );

      const selectedBundleColors = [
        ...form.querySelectorAll(
          '[data-bundle-color]:checked'
        )
      ].filter(
        (input) => !input.disabled
      );

      if (
        selectedBundleColors.length !==
        expectedQuantity
      ) {
        throw new Error(
          'Lütfen paketteki her çanta için bir renk seçin.'
        );
      }

      const packageName =
        form.querySelector(
          '[data-bundle-property]'
        )?.value ||
        `${expectedQuantity} Adet`;

      const group =
        `fd-crossbody-${Date.now()}`;

      const colorSummary =
        selectedBundleColors
          .map(
            (input, index) =>
              `${index + 1}. Çanta: ${input.value}`
          )
          .join(' | ');

      const cartItems =
        selectedBundleColors.map(
          (input, index) => ({
            id: Number(
              input.dataset.variantId
            ),
            quantity: 1,
            properties: {
              Paket: packageName,
              'Çanta Rengi':
                input.value,
              'pmpr_cart_item':
                group,
              [`${index + 1}. Çanta Rengi`]:
                input.value,
              '_Paket Sırası':
                `${index + 1}/${expectedQuantity}`,
            },
          })
        );

      /*
       * EasySell also reads the product form's base
       * variant and quantity while rebuilding its
       * cart/order payload.
       *
       * Keep those values aligned with the selected
       * bundle, even though each bundle item is sent
       * separately.
       */
      const variantInput =
        form.querySelector(
          'input[name="id"]'
        );

      const quantityInput =
        form.querySelector(
          'input[name="quantity"]'
        );

      if (variantInput) {
        variantInput.value =
          String(
            cartItems[0]?.id || ''
          );
      }

      if (quantityInput) {
        quantityInput.value =
          String(cartItems.length);
      }

      window.ES_ADDITIONAL_DETAILS = {
        fd_crossbody_colors: {
          title:
            'Çanta Renkleri',
          value:
            colorSummary,
        },
      };

      const walletToggle =
        form.querySelector(
          '[data-wallet-addon-toggle]'
        );

      const walletColor =
        form.querySelector(
          '[data-wallet-addon-color]'
        );

      if (walletToggle?.checked) {
        const walletOption =
          walletColor?.selectedOptions?.[0];

        if (
          !walletOption ||
          walletOption.disabled ||
          walletOption.dataset.available ===
            'false'
        ) {
          throw new Error(
            'Ek cüzdan için stokta bir renk seçin.'
          );
        }

        cartItems.push({
          id: Number(
            walletOption.value
          ),
          quantity: 1,
          properties: {
            'Ek Ürün':
              'Fashion Viral Cüzdan',
            'Cüzdan Rengi':
              walletOption.dataset.color ||
              walletOption.textContent.trim(),
            'Çanta Eklenti Fiyatı':
              '699 TL',
          },
        });
      }

      window.pmpr_cart_item =
        cartItems;

      window.ES_CLOSEST_FORM =
        form;

      for (
        let attempt = 0;
        attempt < 40;
        attempt += 1
      ) {
        if (
          typeof window.openTysloForm ===
          'function'
        ) {
          await Promise.resolve(
            window.openTysloForm()
          );
          return;
        }

        const appButton =
          root.querySelector(
            '.es-popup-button-product, #es-popup-button'
          );

        if (
          appButton &&
          appButton !== button
        ) {
          appButton.click();
          return;
        }

        const embeddedForm =
          document.querySelector(
            '#easysell .es-form'
          );

        if (embeddedForm) {
          document
            .getElementById('easysell')
            ?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });

          return;
        }

        await new Promise(
          (resolve) =>
            window.setTimeout(
              resolve,
              100
            )
        );
      }

      throw new Error(
        'EasySell kapıda ödeme formu yüklenemedi. Lütfen tekrar deneyin.'
      );

    } catch (error) {
      console.error(
        '[FD EasySell] Form could not be opened:',
        error
      );

      showCartFormError(
        form,
        error?.message
      );

    } finally {
      button.disabled =
        wasDisabled;

      button.removeAttribute(
        'aria-busy'
      );
    }
  }

  function initCountdown(root) {
    const duration =
      Number(
        root.dataset.duration ||
        3600
      ) * 1000;

    const key =
      'fd-launch-countdown';

    let end = 0;

    try {
      end = Number(
        window.localStorage.getItem(
          key
        )
      );
    } catch (error) {
      end = 0;
    }

    const persistEnd = () => {
      try {
        window.localStorage.setItem(
          key,
          String(end)
        );
      } catch (error) {
        // Storage can be unavailable.
      }
    };

    if (
      !end ||
      end < Date.now()
    ) {
      end =
        Date.now() +
        duration;

      persistEnd();
    }

    const render = () => {
      const remaining =
        Math.max(
          0,
          end - Date.now()
        );

      const hours =
        Math.floor(
          remaining /
          3600000
        );

      const minutes =
        Math.floor(
          (remaining %
            3600000) /
          60000
        );

      const seconds =
        Math.floor(
          (remaining %
            60000) /
          1000
        );

      root
        .querySelector(
          '[data-hours]'
        )
        ?.replaceChildren(
          String(hours)
            .padStart(2, '0')
        );

      root
        .querySelector(
          '[data-minutes]'
        )
        ?.replaceChildren(
          String(minutes)
            .padStart(2, '0')
        );

      root
        .querySelector(
          '[data-seconds]'
        )
        ?.replaceChildren(
          String(seconds)
            .padStart(2, '0')
        );

      if (remaining <= 0) {
        end =
          Date.now() +
          duration;

        persistEnd();
      }
    };

    render();

    window.setInterval(
      render,
      1000
    );
  }

  function initGallery(root) {
    const main =
      root.querySelector(
        '[data-gallery-main]'
      );

    const thumbs = [
      ...root.querySelectorAll(
        '[data-gallery-thumb]'
      ),
    ];

    let activeIndex =
      Math.max(
        0,
        thumbs.findIndex(
          (thumb) =>
            thumb.classList.contains(
              'is-active'
            )
        )
      );

    const select = (index) => {
      if (
        !main ||
        !thumbs.length
      ) return;

      activeIndex =
        (index +
          thumbs.length) %
        thumbs.length;

      const button =
        thumbs[activeIndex];

      main.classList.add(
        'is-changing'
      );

      const preload =
        new Image();

      preload.onload = () => {
        main.src =
          button.dataset.src;

        main.srcset = '';

        requestAnimationFrame(
          () =>
            main.classList.remove(
              'is-changing'
            )
        );
      };

      preload.src =
        button.dataset.src;

      thumbs.forEach(
        (item) =>
          item.classList.remove(
            'is-active'
          )
      );

      button.classList.add(
        'is-active'
      );

      button.scrollIntoView({
        behavior: 'smooth',
        inline: 'nearest',
        block: 'nearest',
      });
    };

    thumbs.forEach(
      (button, index) =>
        button.addEventListener(
          'click',
          () =>
            select(index)
        )
    );

    let pointerStart =
      null;

    root
      .querySelector(
        '.fd-gallery__stage'
      )
      ?.addEventListener(
        'pointerdown',
        (event) => {
          pointerStart =
            event.clientX;
        }
      );

    root
      .querySelector(
        '.fd-gallery__stage'
      )
      ?.addEventListener(
        'pointerup',
        (event) => {
          if (
            pointerStart ===
            null
          ) return;

          const distance =
            event.clientX -
            pointerStart;

          if (
            Math.abs(distance) >
            40
          ) {
            select(
              activeIndex +
                (distance < 0
                  ? 1
                  : -1)
            );
          }

          pointerStart =
            null;
        }
      );
  }


  /*
   * ============================================================
   * UNIVERSAL SHOPIFY CARD CHECKOUT
   * ============================================================
   *
   * Bu handler normal Shopify ürün formlarında da çalışır.
   * Fashion Dorella'nın özel bundle formunda mevcut
   * checkoutWithCard() akışı korunur.
   */
  async function universalCardCheckout(form, button) {
    if (!form || form.dataset.fdUniversalCardBusy === 'true') return;

    const variantInput = form.querySelector('input[name="id"]');
    if (!variantInput?.value) {
      showCartFormError(form, 'Lütfen bir ürün seçin.');
      return;
    }

    const quantityInput = form.querySelector('input[name="quantity"]');
    const quantity = Math.max(1, Number(quantityInput?.value || 1));

    form.dataset.fdUniversalCardBusy = 'true';
    const wasDisabled = Boolean(button?.disabled);

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      const formData = new FormData(form);

      // Shopify'nin ürün formundaki varyant + miktar + properties
      // değerlerini koru. Bundle olmayan standart ürünler burada
      // doğrudan Shopify cart'a eklenir.
      formData.set('id', String(variantInput.value));
      formData.set('quantity', String(quantity));

      const response = await fetch(shopRoute('cart/add.js'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result.description ||
          result.message ||
          'Ürün checkout için sepete eklenemedi.'
        );
      }

      // EasySell açılmaz, cart drawer açılmaz.
      // Shopify checkout'a doğrudan geç.
      window.location.assign(shopRoute('checkout'));
    } catch (error) {
      console.error('[FD Universal Card Checkout] Failed:', error);
      showCartFormError(
        form,
        error?.message ||
        'Kredi kartı ödeme sayfası açılamadı. Lütfen tekrar deneyin.'
      );
    } finally {
      form.dataset.fdUniversalCardBusy = 'false';

      if (button) {
        button.disabled = wasDisabled;
        button.removeAttribute('aria-busy');
      }
    }
  }

  /*
   * Sayfada hangi template kullanılırsa kullanılsın,
   * data-fd-card-checkout butonunu ilgili Shopify formuna bağla.
   *
   * Özel Fashion Dorella ürünlerinde initProduct() kendi
   * bundle-aware checkoutWithCard() fonksiyonunu kullanır.
   */
  function initUniversalCardButtons(scope = document) {
    scope.querySelectorAll?.('[data-fd-card-checkout]').forEach((button) => {
      if (button.dataset.fdCardUniversalBound === 'true') return;

      const productRoot =
        button.closest('[data-fd-product]') ||
        button.closest('.buy-buttons-block') ||
        button.closest('product-form-component') ||
        button.parentElement;

      const form =
        productRoot?.querySelector('form[data-fd-product-form]') ||
        button.closest('form') ||
        productRoot?.querySelector('form[data-type="add-to-cart-form"]') ||
        button.closest('product-form-component')?.querySelector('form');

      if (!form) return;

      // Özel FD ürünlerinde initProduct() bağlayacak.
      if (form.matches('form[data-fd-product-form]')) return;

      button.dataset.fdCardUniversalBound = 'true';

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        universalCardCheckout(form, button);
      });
    });
  }

  function initProduct(root) {
    const form =
      root.querySelector(
        'form[data-fd-product-form]'
      );

    const variantInput =
      form?.querySelector(
        'input[name="id"]'
      );

    const quantityInput =
      form?.querySelector(
        'input[name="quantity"]'
      );

    const bundleProperty =
      form?.querySelector(
        '[data-bundle-property]'
      );

    const variantMapNode =
      root.querySelector(
        '[data-fd-variant-map]'
      );

    const currentPrice =
      root.querySelector(
        '[data-current-price]'
      );

    const comparePrice =
      root.querySelector(
        '[data-compare-price]'
      );

    const optionLabel =
      root.querySelector(
        '[data-option-label]'
      );

    const colorRows = [
      ...(form?.querySelectorAll(
        '[data-bundle-color-row]'
      ) || []),
    ];

    const colorError =
      form?.querySelector(
        '[data-bundle-colors-error]'
      );

    const colorSelects = [
      ...(form?.querySelectorAll(
        '[data-color-select]'
      ) || []),
    ];

    const colorBlock =
      form?.querySelector(
        '[data-bundle-colors]'
      );

    const walletAddon =
      form?.querySelector(
        '[data-wallet-addon]'
      );

    const walletToggle =
      form?.querySelector(
        '[data-wallet-addon-toggle]'
      );

    const walletColor =
      form?.querySelector(
        '[data-wallet-addon-color]'
      );

    const walletImage =
      form?.querySelector(
        '[data-wallet-addon-image]'
      );

    const walletStock =
      form?.querySelector(
        '[data-wallet-addon-stock]'
      );

    /*
     * Renk seçim bloğunu seçili paket kartının
     * hemen altına taşı.
     */
    const positionColorBlock = () => {
      const activeBundle =
        root.querySelector(
          '[data-bundle].is-active'
        );

      if (
        !colorBlock ||
        !activeBundle
      ) return;

      if (
        activeBundle.nextElementSibling ===
        colorBlock
      ) return;

      const openSelect =
        colorBlock.querySelector(
          '[data-color-select].is-open'
        );

      if (openSelect) {
        toggleColorSelect(
          openSelect,
          false
        );
      }

      activeBundle.insertAdjacentElement(
        'afterend',
        colorBlock
      );
    };

    const closeColorSelects = (
      except
    ) => {
      colorSelects.forEach(
        (select) => {
          if (
            select === except
          ) return;

          select.classList.remove(
            'is-open'
          );

          select
            .querySelector(
              '[data-color-select-list]'
            )
            ?.setAttribute(
              'hidden',
              ''
            );

          select
            .querySelector(
              '[data-color-select-trigger]'
            )
            ?.setAttribute(
              'aria-expanded',
              'false'
            );
        }
      );
    };

    const toggleColorSelect = (
      select,
      open
    ) => {
      const list =
        select.querySelector(
          '[data-color-select-list]'
        );

      const trigger =
        select.querySelector(
          '[data-color-select-trigger]'
        );

      const shouldOpen =
        open ??
        list?.hasAttribute(
          'hidden'
        );

      if (shouldOpen) {
        closeColorSelects(
          select
        );
      }

      select.classList.toggle(
        'is-open',
        Boolean(shouldOpen)
      );

      if (list) {
        if (shouldOpen) {
          list.removeAttribute(
            'hidden'
          );
        } else {
          list.setAttribute(
            'hidden',
            ''
          );
        }
      }

      trigger?.setAttribute(
        'aria-expanded',
        shouldOpen
          ? 'true'
          : 'false'
      );

      if (shouldOpen) {
        list
          ?.querySelector(
            'input:checked, input'
          )
          ?.focus();
      }
    };

    const renderColorRow = (
      row
    ) => {
      if (!row) return;

      const selected =
        row.querySelector(
          '[data-bundle-color]:checked'
        );

      const placeholder =
        row.dataset.placeholder ||
        'Seçiniz';

      const label =
        selected?.value ||
        placeholder;

      const image =
        selected?.dataset.image ||
        '';

      row.classList.toggle(
        'is-empty',
        !selected
      );

      row
        .querySelector(
          '[data-color-select]'
        )
        ?.classList.toggle(
          'is-empty',
          !selected
        );

      row
        .querySelector(
          '[data-bundle-color-label]'
        )
        ?.replaceChildren(
          label
        );

      row
        .querySelector(
          '[data-color-select-value]'
        )
        ?.replaceChildren(
          label
        );

      [
        row.querySelector(
          '[data-bundle-color-thumb]'
        ),
        row.querySelector(
          '[data-color-select-swatch]'
        ),
      ].forEach(
        (node) => {
          if (!node) return;

          if (image) {
            if (
              node.getAttribute(
                'src'
              ) !== image
            ) {
              node.setAttribute(
                'src',
                image
              );
            }

            node.removeAttribute(
              'hidden'
            );
          } else {
            node.setAttribute(
              'hidden',
              ''
            );
          }
        }
      );
    };

    colorSelects.forEach(
      (select) => {
        const trigger =
          select.querySelector(
            '[data-color-select-trigger]'
          );

        trigger?.addEventListener(
          'click',
          (event) => {
            event.preventDefault();

            if (
              trigger.disabled
            ) return;

            toggleColorSelect(
              select
            );
          }
        );

        select.addEventListener(
          'keydown',
          (event) => {
            if (
              event.key ===
              'Escape'
            ) {
              toggleColorSelect(
                select,
                false
              );

              trigger?.focus();
            }
          }
        );
      }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (
          !event.target.closest(
            '[data-color-select]'
          )
        ) {
          closeColorSelects(
            null
          );
        }
      }
    );

    let unitPrice =
      Number(
        root.dataset.price ||
        0
      );

    let compareAt =
      Number(
        root.dataset.compare ||
        0
      );

    const currency =
      root.dataset.currency ||
      'TRY';

    const formatProductMoney =
      (cents) =>
        root.dataset.moneyStyle ===
        'trailing-tl'
          ? `${new Intl.NumberFormat(
              'tr-TR',
              {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }
            ).format(
              Number(cents || 0) /
                100
            )} TL`
          : money(
              cents,
              currency
            );

    let variantMap = [];

    try {
      variantMap =
        JSON.parse(
          variantMapNode?.textContent ||
            '[]'
        );
    } catch (error) {
      console.error(
        '[FD product] Variant map could not be parsed.',
        error
      );
    }

    const updateBundles = () => {
      root
        .querySelectorAll(
          '[data-bundle]'
        )
        .forEach(
          (bundle) => {
            const multiplier =
              Number(
                bundle.dataset
                  .multiplier ||
                  1
              );

            const compareMultiplier =
              Number(
                bundle.dataset
                  .compareMultiplier ||
                  bundle.dataset
                    .quantity ||
                  1
              );

            const displayPrice =
              Number(
                bundle.dataset
                  .displayPrice ||
                  Math.round(
                    unitPrice *
                      multiplier
                  )
              );

            const displayCompare =
              Number(
                bundle.dataset
                  .displayCompare ||
                  Math.round(
                    (compareAt ||
                      unitPrice) *
                      compareMultiplier
                  )
              );

            const priceText =
              bundle.hasAttribute(
                'data-display-price-text'
              )
                ? bundle.dataset
                    .displayPriceText
                : money(
                    displayPrice,
                    currency
                  );

            const compareText =
              bundle.hasAttribute(
                'data-display-compare-text'
              )
                ? bundle.dataset
                    .displayCompareText
                : money(
                    displayCompare,
                    currency
                  );

            bundle
              .querySelector(
                '[data-bundle-price]'
              )
              ?.replaceChildren(
                priceText
              );

            bundle
              .querySelector(
                '[data-bundle-compare]'
              )
              ?.replaceChildren(
                compareText
              );
          }
        );
    };

    const syncVariantSelection = () => {
      if (
        !form ||
        !variantInput
      ) return;

      const colorRadio =
        root.querySelector(
          '[data-variant]:checked'
        );

      const activeBundle =
        root.querySelector(
          '[data-bundle].is-active'
        );

      const bundleQuantity =
        Math.max(
          1,
          Number(
            activeBundle?.dataset
              .quantity ||
              1
          )
        );

      colorRows.forEach(
        (row) => {
          const enabled =
            Number(
              row.dataset.itemIndex
            ) <= bundleQuantity;

          row.hidden =
            !enabled;

          row
            .querySelectorAll(
              '[data-bundle-color]'
            )
            .forEach(
              (input) => {
                input.disabled =
                  !enabled ||
                  input.dataset
                    .available ===
                    'false';
              }
            );

          const trigger =
            row.querySelector(
              '[data-color-select-trigger]'
            );

          if (trigger) {
            trigger.disabled =
              !enabled;
          }

          if (!enabled) {
            const select =
              row.querySelector(
                '[data-color-select]'
              );

            if (select) {
              toggleColorSelect(
                select,
                false
              );
            }
          }

          renderColorRow(row);
        }
      );

      const selectedBundleColors =
        colorRows
          .filter(
            (row) => !row.hidden
          )
          .map(
            (row) =>
              row.querySelector(
                '[data-bundle-color]:checked'
              )
          )
          .filter(Boolean);

      const colorsComplete =
        selectedBundleColors.length ===
        bundleQuantity;

      positionColorBlock();

      const firstBundleColor =
        selectedBundleColors[0];

      const color =
        firstBundleColor?.dataset
          .color ||
        colorRadio?.dataset.color ||
        colorRadio?.dataset.title ||
        '';

      const bundleTitle =
        activeBundle?.dataset.title ||
        `${activeBundle?.dataset.quantity || 1} Adet`;

      if (!variantMap.length) {
        variantInput.value =
          firstBundleColor?.dataset
            .variantId ||
          firstBundleColor?.value ||
          '';

        if (quantityInput) {
          quantityInput.value =
            String(
              bundleQuantity
            );
        }
      } else {
        const selectedVariant =
          variantMap.find(
            (variant) =>
              variant.color ===
                color &&
              variant.bundle ===
                bundleTitle
          );

        variantInput.value =
          selectedVariant?.id
            ? String(
                selectedVariant.id
              )
            : '';

        if (quantityInput) {
          quantityInput.value =
            '1';
        }
      }

      const selectedVariant =
        variantMap.find(
          (variant) =>
            variant.color ===
              color &&
            variant.bundle ===
              bundleTitle
        );

      const unavailable =
        !colorsComplete ||
        !variantInput.value ||
        (
          variantMap.length > 0 &&
          (
            !selectedVariant ||
            selectedVariant.available ===
              false
          )
        );

      /*
       * Kredi kartı butonunu da mevcut ürün
       * uygunluk sistemiyle birlikte yönetiyoruz.
       */
      root
        .querySelectorAll(
          '.fd-easysell-source, [data-fd-easysell-trigger], [data-fd-cart-submit], [data-fd-card-checkout]'
        )
        .forEach(
          (button) => {
            button.disabled =
              unavailable;
          }
        );

      if (colorError) {
        colorError.hidden =
          colorsComplete;
      }
    };

    const syncWalletAddon = () => {
      if (
        !walletAddon ||
        !walletToggle ||
        !walletColor
      ) return;

      const selected =
        walletColor
          .selectedOptions?.[0];

      const available =
        Boolean(
          selected &&
          !selected.disabled &&
          selected.dataset
            .available !==
            'false'
        );

      const anyAvailable =
        [
          ...walletColor.options,
        ].some(
          (option) =>
            !option.disabled &&
            option.dataset
              .available !==
              'false'
        );

      walletColor.disabled =
        !anyAvailable;

      walletToggle.disabled =
        !anyAvailable;

      if (!available) {
        walletToggle.checked =
          false;
      }

      walletAddon.classList.toggle(
        'is-selected',
        Boolean(
          walletToggle.checked &&
          available
        )
      );

      walletAddon.classList.toggle(
        'is-unavailable',
        !anyAvailable
      );

      if (
        walletImage &&
        selected?.dataset.image
      ) {
        walletImage.src =
          selected.dataset.image;
      }

      if (walletStock) {
        walletStock.textContent =
          available
            ? 'Stokta'
            : 'Stok yok';
      }
    };

    root
      .querySelectorAll(
        '[data-variant]'
      )
      .forEach(
        (radio) => {
          radio.addEventListener(
            'change',
            () => {
              if (!radio.checked)
                return;

              unitPrice =
                Number(
                  radio.dataset.price ||
                    unitPrice
                );

              compareAt =
                Number(
                  radio.dataset.compare ||
                    0
                );

              currentPrice?.replaceChildren(
                formatProductMoney(
                  unitPrice
                )
              );

              if (comparePrice) {
                comparePrice.replaceChildren(
                  compareAt >
                    unitPrice
                    ? formatProductMoney(
                        compareAt
                      )
                    : ''
                );

                comparePrice.hidden =
                  !(
                    compareAt >
                    unitPrice
                  );
              }

              if (optionLabel) {
                optionLabel.replaceChildren(
                  radio.dataset.title ||
                    ''
                );
              }

              const firstColor =
                form?.querySelector(
                  '[data-bundle-color-row][data-item-index="1"]'
                );

              const matchingFirstColor =
                firstColor?.querySelector(
                  `[data-bundle-color][data-color="${CSS.escape(
                    radio.dataset.color ||
                      radio.dataset.title ||
                      ''
                  )}"]`
                );

              if (matchingFirstColor) {
                matchingFirstColor.checked =
                  true;
              }

              if (radio.dataset.image) {
                const main =
                  root.querySelector(
                    '[data-gallery-main]'
                  );

                if (main) {
                  main.src =
                    radio.dataset.image;

                  main.srcset = '';
                }
              }

              updateBundles();
              syncVariantSelection();
            }
          );
        }
      );

    form
      ?.querySelectorAll(
        '[data-bundle-color]'
      )
      .forEach(
        (radio) => {
          radio
            .closest(
              '.fd-color-select__option'
            )
            ?.addEventListener(
              'click',
              () => {
                if (
                  radio.disabled ||
                  !radio.checked
                ) return;

                const select =
                  radio.closest(
                    '[data-color-select]'
                  );

                if (select) {
                  toggleColorSelect(
                    select,
                    false
                  );

                  select
                    .querySelector(
                      '[data-color-select-trigger]'
                    )
                    ?.focus();
                }
              }
            );

          radio.addEventListener(
            'change',
            () => {
              if (!radio.checked)
                return;

              const row =
                radio.closest(
                  '[data-bundle-color-row]'
                );

              renderColorRow(
                row
              );

              const select =
                row?.querySelector(
                  '[data-color-select]'
                );

              if (select) {
                toggleColorSelect(
                  select,
                  false
                );

                select
                  .querySelector(
                    '[data-color-select-trigger]'
                  )
                  ?.focus();
              }

              if (
                row?.dataset
                  .itemIndex ===
                '1'
              ) {
                const mainColor =
                  root.querySelector(
                    `[data-variant][data-color="${CSS.escape(
                      radio.dataset
                        .color ||
                        ''
                    )}"]`
                  );

                if (
                  mainColor &&
                  !mainColor.checked
                ) {
                  mainColor.checked =
                    true;

                  mainColor.dispatchEvent(
                    new Event(
                      'change',
                      {
                        bubbles:
                          true,
                      }
                    )
                  );

                  return;
                }
              }

              syncVariantSelection();
            }
          );
        }
      );

    root
      .querySelectorAll(
        '[data-bundle]'
      )
      .forEach(
        (bundle) => {
          bundle.addEventListener(
            'click',
            () => {
              root
                .querySelectorAll(
                  '[data-bundle]'
                )
                .forEach(
                  (item) =>
                    item.classList.remove(
                      'is-active'
                    )
                );

              bundle.classList.add(
                'is-active'
              );

              const radio =
                bundle.querySelector(
                  'input[type="radio"]'
                );

              if (radio) {
                radio.checked =
                  true;
              }

              if (bundleProperty) {
                bundleProperty.value =
                  bundle.dataset.title ||
                  `${bundle.dataset.quantity || 1} Adet`;
              }

              syncVariantSelection();
            }
          );
        }
      );

    walletToggle?.addEventListener(
      'change',
      syncWalletAddon
    );

    walletColor?.addEventListener(
      'change',
      syncWalletAddon
    );

    /*
     * ========================================================
     * EASySELL / KAPIDA ÖDEME
     * ========================================================
     *
     * Mevcut sistem aynen korunuyor.
     */
    root
      .querySelector(
        '[data-fd-easysell-trigger]'
      )
      ?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          if (
            !form ||
            !variantInput?.value
          ) return;

          openEasySellForm(
            root,
            form,
            event.currentTarget
          );
        }
      );

    /*
     * ========================================================
     * KREDİ KARTI BUTONU
     * ========================================================
     *
     * Sadece yeni kredi kartı butonunu dinler.
     *
     * EasySell butonuna hiçbir müdahale yok.
     */
    root
      .querySelector(
        '[data-fd-card-checkout]'
      )
      ?.addEventListener(
        'click',
        (event) => {
          event.preventDefault();

          if (
            !form ||
            !variantInput?.value
          ) return;

          checkoutWithCard(
            root,
            form,
            event.currentTarget
          );
        }
      );

    updateBundles();
    syncVariantSelection();
    syncWalletAddon();

    const bundleTimer =
      root.querySelector(
        '[data-bundle-countdown]'
      );

    if (bundleTimer) {
      let remaining =
        15 * 60;

      const tick = () => {
        remaining =
          remaining > 0
            ? remaining - 1
            : 15 * 60;

        bundleTimer.textContent =
          `${String(
            Math.floor(
              remaining / 60
            )
          ).padStart(2, '0')}:${String(
            remaining % 60
          ).padStart(2, '0')}`;
      };

      tick();

      window.setInterval(
        tick,
        1000
      );
    }
  }

  function initFeatured(root) {
    const select =
      root.querySelector(
        '[data-featured-variant]'
      );

    const input =
      root.querySelector(
        'input[name="id"]'
      );

    select?.addEventListener(
      'change',
      () => {
        const option =
          select.selectedOptions[0];

        if (input) {
          input.value =
            option.value;
        }

        if (
          root.dataset
            .useProductPrice ===
          'true'
        ) {
          root
            .querySelector(
              '[data-featured-price]'
            )
            ?.replaceChildren(
              money(
                option.dataset.price,
                root.dataset
                  .currency ||
                  'TRY'
              )
            );
        }

        const image =
          root.querySelector(
            '[data-featured-image]'
          );

        if (
          image &&
          option.dataset.image
        ) {
          image.src =
            option.dataset.image;

          image.srcset = '';
        }
      }
    );
  }

  function initComparison(root) {
    const range =
      root.querySelector(
        'input[type="range"]'
      );

    const after =
      root.querySelector(
        '.fd-comparison__after'
      );

    const line =
      root.querySelector(
        '.fd-comparison__line'
      );

    const handle =
      root.querySelector(
        '.fd-comparison__handle'
      );

    range?.addEventListener(
      'input',
      () => {
        const value =
          Number(
            range.value
          );

        if (after) {
          after.style.clipPath =
            `inset(0 0 0 ${value}%)`;
        }

        if (line) {
          line.style.left =
            `${value}%`;
        }

        if (handle) {
          handle.style.left =
            `${value}%`;
        }
      }
    );
  }

  function initVision(root) {
    root
      .querySelectorAll(
        '[data-fd-vision-tab]'
      )
      .forEach(
        (tab) => {
          tab.addEventListener(
            'click',
            () => {
              const target =
                tab.dataset
                  .fdVisionTab;

              root
                .querySelectorAll(
                  '[data-fd-vision-tab]'
                )
                .forEach(
                  (item) => {
                    const active =
                      item === tab;

                    item.classList.toggle(
                      'is-active',
                      active
                    );

                    item.setAttribute(
                      'aria-selected',
                      String(
                        active
                      )
                    );
                  }
                );

              root
                .querySelectorAll(
                  '[data-fd-vision-panel]'
                )
                .forEach(
                  (panel) => {
                    const active =
                      panel.dataset
                        .fdVisionPanel ===
                      target;

                    panel.hidden =
                      !active;

                    panel.classList.toggle(
                      'is-active',
                      active
                    );

                    panel.classList.remove(
                      'is-entering'
                    );

                    if (active) {
                      requestAnimationFrame(
                        () =>
                          panel.classList.add(
                            'is-entering'
                          )
                      );
                    }
                  }
                );
            }
          );
        }
      );
  }

  const revealObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach(
              (entry) => {
                if (
                  !entry.isIntersecting
                ) return;

                entry.target.classList.add(
                  'is-visible'
                );

                revealObserver.unobserve(
                  entry.target
                );
              }
            );
          },
          {
            threshold: 0.01,
            rootMargin:
              '0px 0px 10% 0px',
          }
        )
      : null;

  let revealFrame = 0;

  function revealVisibleElements() {
    revealFrame = 0;

    document
      .querySelectorAll(
        '[data-fd-reveal].is-reveal-pending:not(.is-visible)'
      )
      .forEach(
        (element) => {
          const rect =
            element.getBoundingClientRect();

          if (
            rect.top >
              window.innerHeight *
                1.1 ||
            rect.bottom <
              -window.innerHeight *
                0.1
          ) return;

          element.classList.add(
            'is-visible'
          );

          revealObserver?.unobserve(
            element
          );
        }
      );
  }

  function scheduleRevealCheck() {
    if (revealFrame) return;

    revealFrame =
      window.requestAnimationFrame(
        revealVisibleElements
      );
  }

  function boot(scope = document) {
    scope
      .querySelectorAll(
        'form[data-fd-cart-form]:not([data-fd-cart-ready])'
      )
      .forEach(
        (form) => {
          form.dataset.fdCartReady =
            'true';

          initCartForm(form);
        }
      );

    scope
      .querySelectorAll(
        '[data-fd-countdown]:not([data-ready])'
      )
      .forEach(
        (root) => {
          root.dataset.ready =
            'true';

          initCountdown(root);
        }
      );

    scope
      .querySelectorAll(
        '[data-fd-product]:not([data-ready])'
      )
      .forEach(
        (root) => {
          root.dataset.ready =
            'true';

          initGallery(root);
          initProduct(root);
        }
      );

    scope
      .querySelectorAll(
        '[data-fd-featured]:not([data-ready])'
      )
      .forEach(
        (root) => {
          root.dataset.ready =
            'true';

          initFeatured(root);
        }
      );

    scope
      .querySelectorAll(
        '[data-fd-comparison]:not([data-ready])'
      )
      .forEach(
        (root) => {
          root.dataset.ready =
            'true';

          initComparison(root);
        }
      );

    scope
      .querySelectorAll(
        '.fd-vision:not([data-ready])'
      )
      .forEach(
        (root) => {
          root.dataset.ready =
            'true';

          initVision(root);
        }
      );

    scope
      .querySelectorAll(
        '[data-fd-reveal]:not([data-reveal-ready])'
      )
      .forEach(
        (element) => {
          element.dataset.revealReady =
            'true';

          element.classList.add(
            'is-reveal-pending'
          );

          if (revealObserver) {
            revealObserver.observe(
              element
            );
          } else {
            element.classList.add(
              'is-visible'
            );
          }
        }
      );

    scheduleRevealCheck();
  }

  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => boot()
    );
  } else {
    boot();
  }

  window.addEventListener(
    'scroll',
    scheduleRevealCheck,
    { passive: true }
  );

  window.addEventListener(
    'resize',
    scheduleRevealCheck
  );

  document.addEventListener(
    'shopify:section:load',
    (event) =>
      boot(event.target)
  );

  document.addEventListener(
    'click',
    (event) => {
      const toggle =
        event.target.closest?.(
          '[data-fd-cart-toggle]'
        );

      if (toggle) {
        event.preventDefault();
        event.stopImmediatePropagation();

        toggleCartDrawer();

        return;
      }

      const close =
        event.target.closest?.(
          '[data-fd-cart-close]'
        );

      if (close) {
        event.preventDefault();
        event.stopImmediatePropagation();

        closeCartDrawer();
      }
    },
    true
  );

  document.addEventListener(
    'theme-drawer:open',
    (event) => {
      if (
        event.target?.id ===
        'cart-drawer'
      ) {
        syncCartTriggerState(
          true
        );
      }
    }
  );

  document.addEventListener(
    'theme-drawer:close',
    (event) => {
      if (
        event.target?.id ===
        'cart-drawer'
      ) {
        syncCartTriggerState(
          false
        );
      }
    }
  );

  /*
   * Universal card button boot.
   * Delegation means Shopify section re-renders / app markup changes
   * do not remove the click behavior.
   */
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('[data-fd-card-checkout]');
    if (!button) return;

    // Özel FD product handler zaten varsa ikinci kez çalıştırma.
    if (button.closest('[data-fd-product]')) return;

    const component = button.closest('product-form-component');
    const form =
      button.closest('form') ||
      component?.querySelector('form[data-type="add-to-cart-form"]');

    if (!form) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    universalCardCheckout(form, button);
  }, true);

  initUniversalCardButtons(document);


})();